import {Component} from "../enum/Component";
import {SchemaUtils} from "../util/SchemaUtils";
import {DateUtils} from "../util/DateUtils";
import moment from "moment";
import * as formulajs from "@formulajs/formulajs";
import {PmtFunctionProperties} from "../model/PmtFunctionProperties";
import {StringUtils} from "../util/StringUtils";
import {FormulaTotal} from "../model/FormulaTotal";
import {ValidationUtil} from "../util/ValidationUtil";

export class ControlValueFactory{

    /**
     *
     * @param component
     * @param data
     * @param scope
     * @param jsonForms
     * @param active
     */
    public static getControlValue(component: Component, data: any, scope: string, jsonForms: any, active: boolean){
        let value;
        switch (component){
            case Component.Number:
            case Component.Range:
                value = !ValidationUtil.isEmptyValue(data) ? data : "";
                if(!active){
                    let defaultValue = SchemaUtils.getDefaultValue(scope, jsonForms);
                    value = defaultValue;
                    SchemaUtils.resetDataValue(scope, jsonForms, defaultValue);
                }
                return value;
            case Component.Rating:
                // 0 is not a selectable value on the rating scale (MUI range is 1..max),
                // so treat schema-default 0 as "unset"
                value = !ValidationUtil.isEmptyValue(data) && Number(data) !== 0 ? data : null;
                if(!active){
                    let defaultValue = SchemaUtils.getDefaultValue(scope, jsonForms);
                    value = defaultValue !== undefined && Number(defaultValue) !== 0
                        ? defaultValue
                        : null;
                    SchemaUtils.resetDataValue(scope, jsonForms, value);
                }
                return value;
            case Component.Formula:
                return this.calculateTotal(jsonForms, scope);
            case Component.Checkbox:
            case Component.Autocomplete:
            case Component.Radio:
            case Component.Select:
            case Component.Text:
            case Component.Phone:
            case Component.Email:
            case Component.TextArea:
            case Component.FileUpload:
            case Component.Signature:
                value = data;
                if(!active){
                    let defaultValue = SchemaUtils.getDefaultValue(scope, jsonForms);
                    value = defaultValue;
                    SchemaUtils.resetDataValue(scope, jsonForms, defaultValue);
                }
                return value;
            case Component.Date:
                value = (!ValidationUtil.isEmptyValue(data) &&
                    data !== DateUtils.invalidDateText &&
                    active) ?
                    data :
                    "";
                if(!active){
                    let defaultValue = SchemaUtils.getDefaultValue(scope, jsonForms);
                    value = defaultValue;
                    SchemaUtils.resetDataValue(scope, jsonForms, defaultValue);
                }
                return value;
            case Component.Time:
                value = !ValidationUtil.isEmptyValue(data) &&
                data !== DateUtils.invalidDateText &&
                active ? data : "";
                if(!active){
                    let defaultValue = SchemaUtils.getDefaultValue(scope, jsonForms);
                    value = defaultValue;
                    SchemaUtils.resetDataValue(scope, jsonForms, defaultValue);
                }
                return value;
            case Component.Columns:
                value = !ValidationUtil.isEmptyValue(data) ? data : [];
                return value;
            default:
                return "";
        }
    }

    private static calculateTotal(jsonForms: any, scope: string): FormulaTotal|undefined {
        const properties = scope.split("/properties/");
        const page = properties[1];
        const component = properties[2];
        const type = properties[3];
        let pmtData = jsonForms.core!.schema.properties[page].properties[component].properties[type].pmt
        if(pmtData !== undefined){
            return this.calculatePmtTotal(pmtData, jsonForms);
        }
        return undefined;
    }

    private static calculatePmtTotal(pmtData: any, jsonForms: any): FormulaTotal|undefined {
        const pmtConfiguration = PmtFunctionProperties.fromJSON(pmtData);
        const interestRateField = pmtConfiguration.interestRateField;
        const numberOfPaymentsField = pmtConfiguration.numberOfPaymentsField;
        const loanAmountField = pmtConfiguration.loanAmountField;
        const paymentPeriod = pmtConfiguration.paymentPeriod;
        let rate = 0, nper = 0, pv = 0;
        for (const [pageKey] of Object.entries(jsonForms.core!.schema.properties)) {
            let pageProperties = jsonForms.core!.schema.properties[pageKey].properties;
            for (const [elementKey] of Object.entries(pageProperties)) {
                let id = jsonForms.core!.schema.properties[pageKey].properties[elementKey].uniqueId;
                let interestRateIdsEqual = StringUtils.compareGuid(id || "", interestRateField);
                if (interestRateIdsEqual) {
                    rate = jsonForms.core!.data[pageKey][elementKey].number ||
                        jsonForms.core!.data[pageKey][elementKey].range || 0;
                    rate = (rate / 100) / paymentPeriod;
                }
                let numberOfPaymentsIdsEqual = StringUtils.compareGuid(id || "", numberOfPaymentsField);
                if (numberOfPaymentsIdsEqual) {
                    nper = jsonForms.core!.data[pageKey][elementKey].number ||
                        jsonForms.core!.data[pageKey][elementKey].range || 0;
                }
                let loanAmountIdsEqual = StringUtils.compareGuid(id || "", loanAmountField);
                if (loanAmountIdsEqual) {
                    pv = jsonForms.core!.data[pageKey][elementKey].number ||
                        jsonForms.core!.data[pageKey][elementKey].range || 0;
                }
            }
        }
        let pmt = formulajs.PMT(rate, nper, -pv, pmtConfiguration.futureValue, pmtConfiguration.paymentsDue);
        if (!(pmt instanceof Error)) {
            if(!isFinite(pmt)){
                pmt = 0;
            }
            return new FormulaTotal((pmt+0), pmtConfiguration.currency);
        }
        return undefined;
    }
}
